// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** policyGate — declared-permission gating via CONTRACT:I4-POLICY-DECISION.1.0.
 *  Before activating a capability the host runs EVERY permission the capability
 *  declares through policy.decide; on any deny it raises ErrPermissionDenied and
 *  registers nothing. Obligations on an allow decision are collected so the host
 *  (or capability) can honor them (## Behavioral Contracts: "Declared permissions,
 *  policy-gated"). Fail-closed: a throwing/rejecting decide() is treated as deny. */

import type {
  HostContext,
  PolicyDecision,
} from "../../contracts-ts/src/index.ts";
import type { DeclaredPermission } from "./types.ts";
import { ErrPermissionDenied } from "./errors.ts";

export interface GateResult {
  /** Obligations accumulated across all permit decisions; MUST be honored. */
  obligations: PolicyDecision["obligations"];
}

/** Gate a capability's declared permissions. Resolves with collected obligations
 *  when every declaration permits; rejects with ErrPermissionDenied otherwise.
 *  An empty/absent declaration set is allowed (the capability needs nothing). */
export async function gate(
  capabilityId: string,
  policy: HostContext["policy"],
  declares: DeclaredPermission[] | undefined,
): Promise<GateResult> {
  const obligations: PolicyDecision["obligations"] = [];
  if (!declares || declares.length === 0) {
    return { obligations };
  }
  for (const req of declares) {
    let decision: PolicyDecision;
    try {
      decision = await policy.decide(req);
    } catch (cause) {
      // Fail-closed: an unavailable / throwing policy denies.
      throw new ErrPermissionDenied(
        capabilityId,
        `policy.decide failed for action "${req.action}" (${String(cause)})`,
      );
    }
    if (decision.effect !== "permit") {
      throw new ErrPermissionDenied(
        capabilityId,
        decision.reason || `action "${req.action}" denied`,
      );
    }
    for (const o of decision.obligations) obligations.push(o);
  }
  return { obligations };
}
